import type { Resource, ResourceTypeHandle } from '@toolproof-core/genesis/_contract/_index';

import { extractResourceTypeHandleFromRole } from '@toolproof-core/lib/_pure/workflow-extraction';

import {
    toConnectorItemCollectionDerivation,
    toEntityItemCollectionDerivation,
} from '@toolproof-core/visualization/derivation/_pure/derivation-values';
import type { CollectionKey } from '@toolproof-core/visualization/_contract/key';
import type {
    ConnectorItemCollectionDerivation,
    ConnectorItemDerivation,
    EntityItemCollectionDerivation,
    EntityItemDerivation,
} from '@toolproof-core/visualization/derivation/_contract/derivation';

import {
    buildCaseCollectionKey,
    buildResourceTypeRadialConnectorCollectionKey,
    buildRoleCollectionKey,
    buildToolRoleConnectorCollectionKey,
    buildWorkflowStateEntryCollectionKey,
    type WorkflowKeyScope,
    type buildMacroStepConnectorCollectionKey,
} from '@/workflow/_pure/keyCodec';
import { getRoleEntriesByDirection, getToolHandle } from '@/workflow/_pure/projectionAccess';
import { toRoleItemDerivation, toToolRoleConnectorItemDerivation } from '@/workflow/_pure/derivation/itemFactories';
import type { BaseConfig } from '@/workflow/_contract/config';
import type { WorkflowStateEntryCollectionSeed as KernelWorkflowStateEntryCollectionSeed } from '@/workflow/_contract/kernel';
import type { WorkflowResourceCollection } from '@/workflow/_contract/modeAugmentation';
import type { RoleDirection } from '@/workflow/_contract/role';

type WorkflowStateEntryCollectionSeed = KernelWorkflowStateEntryCollectionSeed;

export function toResourceCollection(
    resourceTypeHandle: ResourceTypeHandle,
    resources: Resource[]
): WorkflowResourceCollection {
    return {
        resourceCollectionKey: resourceTypeHandle,
        resources,
    };
}

export function deriveRoleItemCollectionDerivations(args: {
    toolResources: Resource[];
    resourceTypeHandles: ReadonlySet<ResourceTypeHandle>;
    config: BaseConfig;
}): EntityItemCollectionDerivation[] {
    const { toolResources, resourceTypeHandles, config } = args;

    const grouped = new Map<ReturnType<typeof buildRoleCollectionKey>, {
        toolHandle: string;
        direction: RoleDirection;
        resourceTypeHandle: ResourceTypeHandle;
        itemDerivations: EntityItemDerivation[];
    }>();

    for (const toolResource of toolResources) {
        const toolHandle = getToolHandle(toolResource);
        if (!toolHandle) continue;

        const directions: RoleDirection[] = ['input', 'output'];
        for (const direction of directions) {
            const roleEntries = getRoleEntriesByDirection(toolResource, direction);
            for (const [roleName, role] of roleEntries) {
                if (role === null || typeof role !== 'object') continue;

                const resourceTypeHandle = extractResourceTypeHandleFromRole(
                    role as Parameters<typeof extractResourceTypeHandleFromRole>[0]
                );
                if (!resourceTypeHandle) continue;
                if (!resourceTypeHandles.has(resourceTypeHandle)) continue;

                const collectionKey = buildRoleCollectionKey({
                    toolHandle,
                    direction,
                    resourceTypeHandle,
                });

                const current = grouped.get(collectionKey) ?? {
                    toolHandle,
                    direction,
                    resourceTypeHandle,
                    itemDerivations: [],
                };

                current.itemDerivations.push(toRoleItemDerivation({
                    toolHandle,
                    direction,
                    resourceTypeHandle,
                    roleName,
                    role,
                    config,
                }));

                grouped.set(collectionKey, current);
            }
        }
    }

    return Array.from(grouped.entries()).map(([itemCollectionKey, entry]) => toEntityItemCollectionDerivation({
        itemCollectionKey,
        itemDerivations: entry.itemDerivations,
    }));
}

export function deriveToolRoleConnectorItemCollectionDerivations(args: {
    toolResources: Resource[];
    resourceTypeHandles: ReadonlySet<ResourceTypeHandle>;
    config: BaseConfig;
}): ConnectorItemCollectionDerivation[] {
    const { toolResources, resourceTypeHandles, config } = args;
    const grouped = new Map<ReturnType<typeof buildToolRoleConnectorCollectionKey>, ConnectorItemDerivation[]>();

    for (const toolResource of toolResources) {
        const toolHandle = getToolHandle(toolResource);
        if (!toolHandle) continue;

        for (const direction of ['input', 'output'] as const satisfies RoleDirection[]) {
            const roleEntries = getRoleEntriesByDirection(toolResource, direction);
            for (const [roleName, role] of roleEntries) {
                if (role === null || typeof role !== 'object') continue;

                const resourceTypeHandle = extractResourceTypeHandleFromRole(
                    role as Parameters<typeof extractResourceTypeHandleFromRole>[0]
                );
                if (!resourceTypeHandle) continue;
                if (!resourceTypeHandles.has(resourceTypeHandle)) continue;

                const collectionKey = buildToolRoleConnectorCollectionKey({ toolHandle });
                const current = grouped.get(collectionKey) ?? [];
                current.push(toToolRoleConnectorItemDerivation({
                    toolHandle,
                    direction,
                    resourceTypeHandle,
                    roleName,
                    config,
                }));
                grouped.set(collectionKey, current);
            }
        }
    }

    return Array.from(grouped.entries()).map(([itemCollectionKey, itemDerivations]) => toConnectorItemCollectionDerivation({
        itemCollectionKey,
        itemDerivations,
    }));
}

export function buildCaseItemCollectionDerivations(args: {
    itemDerivationsByOwnerMacroStepKey: ReadonlyMap<string, EntityItemDerivation[]>;
    scope: WorkflowKeyScope;
}): EntityItemCollectionDerivation[] {
    return Array.from(args.itemDerivationsByOwnerMacroStepKey.entries()).map(([ownerMacroStepKey, itemDerivations]) => toEntityItemCollectionDerivation({
        itemCollectionKey: buildCaseCollectionKey({ ...args.scope, ownerMacroStepKey }),
        itemDerivations,
    }));
}

export function buildWorkflowStateEntryItemCollectionDerivations(args: {
    seeds: WorkflowStateEntryCollectionSeed[];
    scope: WorkflowKeyScope;
}): EntityItemCollectionDerivation[] {
    return args.seeds.map((seed) => toEntityItemCollectionDerivation({
        itemCollectionKey: buildWorkflowStateEntryCollectionKey({
            ...args.scope,
            stepKey: seed.stepKey,
            resourceTypeHandle: seed.resourceTypeHandle,
            direction: seed.direction,
        }),
        itemDerivations: seed.itemDerivations,
    }));
}

export function buildMacroStepConnectorItemCollectionDerivations(args: {
    itemDerivationsByCollectionKey: ReadonlyMap<ReturnType<typeof buildMacroStepConnectorCollectionKey>, ConnectorItemDerivation[]>;
}): ConnectorItemCollectionDerivation[] {
    return Array.from(args.itemDerivationsByCollectionKey.entries()).map(([itemCollectionKey, itemDerivations]) => toConnectorItemCollectionDerivation({
        itemCollectionKey,
        itemDerivations,
    }));
}

export function buildWorkflowStateEntryConnectorItemCollectionDerivations(args: {
    itemDerivationsByCollectionKey: ReadonlyMap<CollectionKey<'connector'>, ConnectorItemDerivation[]>;
}): ConnectorItemCollectionDerivation[] {
    return Array.from(args.itemDerivationsByCollectionKey.entries()).map(([itemCollectionKey, itemDerivations]) => toConnectorItemCollectionDerivation({
        itemCollectionKey,
        itemDerivations,
    }));
}

export function buildToolStepItemCollectionDerivation(args: {
    itemCollectionKey: CollectionKey<'entity'>;
    itemDerivations: EntityItemDerivation[];
}): EntityItemCollectionDerivation {
    return toEntityItemCollectionDerivation({
        itemCollectionKey: args.itemCollectionKey,
        itemDerivations: args.itemDerivations,
    });
}

export function buildResourceTypeRadialConnectorItemCollectionDerivation(args: {
    scope: WorkflowKeyScope;
    itemDerivations: ConnectorItemDerivation[];
}): ConnectorItemCollectionDerivation {
    return toConnectorItemCollectionDerivation({
        itemCollectionKey: buildResourceTypeRadialConnectorCollectionKey(args.scope),
        itemDerivations: args.itemDerivations,
    });
}
